import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  orderBy,
} from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  Opportunity,
  OpportunityStage,
  QualifyChecklist,
  StageHistoryEntry,
  KeyDate,
  BidOutcome,
  PostMortemDebrief,
} from "../types";
import { stampWinCredit, stampHarvestOutcome } from "./harvestService";
import { sanitizeForFirestore } from "../utils/firestoreUtils";

const COLLECTION_NAME = "opportunities";

/**
 * Subscribes to the live Opportunity Pipeline in mlc-proposal-hq-db
 */
export function subscribeToOpportunities(
  callback: (opportunities: Opportunity[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, COLLECTION_NAME);
  const q = query(colRef, orderBy("dueDate", "asc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const items: Opportunity[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          client: String(data.client || ""),
          title: String(data.title || ""),
          services: Array.isArray(data.services) ? data.services : [],
          dueDate: String(data.dueDate || ""),
          questionsDueDate: data.questionsDueDate ? String(data.questionsDueDate) : undefined,
          stage: (data.stage as OpportunityStage) || "reviewing",
          owner: String(data.owner || "Unassigned"),
          ownerEmail: data.ownerEmail ? String(data.ownerEmail) : undefined,
          decision: data.decision || null,
          noGoReason: data.noGoReason || null,
          outcome: data.outcome || null,
          outcomeNotes: data.outcomeNotes || null,
          postMortem: data.postMortem || null,
          documentPaths: Array.isArray(data.documentPaths) ? data.documentPaths : [],
          stageHistory: Array.isArray(data.stageHistory) ? data.stageHistory : [],
          qualifyChecklist: data.qualifyChecklist || null,
          requirementsSummary: data.requirementsSummary || null,
          extractedDates: Array.isArray(data.extractedDates) ? data.extractedDates : null,
          qualifySuggestions: Array.isArray(data.qualifySuggestions)
            ? data.qualifySuggestions
            : null,
          planFrozen: typeof data.planFrozen === "boolean" ? data.planFrozen : false,
          planFrozenAt: data.planFrozenAt ? String(data.planFrozenAt) : undefined,
          planFrozenBy: data.planFrozenBy ? String(data.planFrozenBy) : undefined,
          planFrozenByEmail: data.planFrozenByEmail ? String(data.planFrozenByEmail) : undefined,
          planReadinessScore:
            typeof data.planReadinessScore === "number" ? data.planReadinessScore : undefined,
          leadSignOff: data.leadSignOff || null,
          executiveSignOff: data.executiveSignOff || null,
          officialSubmission: data.officialSubmission || null,
          submissionReadinessScore:
            typeof data.submissionReadinessScore === "number"
              ? data.submissionReadinessScore
              : undefined,
          rebidTracking: data.rebidTracking || null,
          originalOpportunityId: data.originalOpportunityId
            ? String(data.originalOpportunityId)
            : undefined,
          createdAt: String(data.createdAt || new Date().toISOString()),
          updatedAt: String(data.updatedAt || new Date().toISOString()),
        };
      });
      callback(items);
    },
    (error) => {
      console.error("Error subscribing to opportunities:", error);
      if (onError) onError(error);
    },
  );
}

/**
 * Subscribes to a single RFP Opportunity document
 */
export function subscribeToOpportunity(
  id: string,
  callback: (opportunity: Opportunity | null) => void,
  onError?: (err: Error) => void,
): () => void {
  const docRef = doc(db, COLLECTION_NAME, id);

  return onSnapshot(
    docRef,
    (docSnap) => {
      if (!docSnap.exists()) {
        callback(null);
        return;
      }
      const data = docSnap.data();
      const opp: Opportunity = {
        id: docSnap.id,
        client: String(data.client || ""),
        title: String(data.title || ""),
        services: Array.isArray(data.services) ? data.services : [],
        dueDate: String(data.dueDate || ""),
        questionsDueDate: data.questionsDueDate ? String(data.questionsDueDate) : undefined,
        stage: (data.stage as OpportunityStage) || "reviewing",
        owner: String(data.owner || "Unassigned"),
        ownerEmail: data.ownerEmail ? String(data.ownerEmail) : undefined,
        decision: data.decision || null,
        noGoReason: data.noGoReason || null,
        outcome: data.outcome || null,
        outcomeNotes: data.outcomeNotes || null,
        postMortem: data.postMortem || null,
        documentPaths: Array.isArray(data.documentPaths) ? data.documentPaths : [],
        stageHistory: Array.isArray(data.stageHistory) ? data.stageHistory : [],
        qualifyChecklist: data.qualifyChecklist || null,
        requirementsSummary: data.requirementsSummary || null,
        extractedDates: Array.isArray(data.extractedDates) ? data.extractedDates : null,
        qualifySuggestions: Array.isArray(data.qualifySuggestions) ? data.qualifySuggestions : null,
        planFrozen: typeof data.planFrozen === "boolean" ? data.planFrozen : false,
        planFrozenAt: data.planFrozenAt ? String(data.planFrozenAt) : undefined,
        planFrozenBy: data.planFrozenBy ? String(data.planFrozenBy) : undefined,
        planFrozenByEmail: data.planFrozenByEmail ? String(data.planFrozenByEmail) : undefined,
        planReadinessScore:
          typeof data.planReadinessScore === "number" ? data.planReadinessScore : undefined,
        leadSignOff: data.leadSignOff || null,
        executiveSignOff: data.executiveSignOff || null,
        officialSubmission: data.officialSubmission || null,
        submissionReadinessScore:
          typeof data.submissionReadinessScore === "number"
            ? data.submissionReadinessScore
            : undefined,
        rebidTracking: data.rebidTracking || null,
        originalOpportunityId: data.originalOpportunityId
          ? String(data.originalOpportunityId)
          : undefined,
        createdAt: String(data.createdAt || new Date().toISOString()),
        updatedAt: String(data.updatedAt || new Date().toISOString()),
      };
      callback(opp);
    },
    (error) => {
      console.error(`Error subscribing to opportunity ${id}:`, error);
      if (onError) onError(error);
    },
  );
}

/**
 * Creates a new RFP Opportunity and initializes the stage history audit trail
 */
export async function createOpportunity(
  data: Omit<Opportunity, "id" | "createdAt" | "updatedAt" | "stageHistory">,
  creator: { displayName: string; email: string },
): Promise<string> {
  const now = new Date().toISOString();
  const initialStage: OpportunityStage = data.stage || "reviewing";

  const initialHistory: StageHistoryEntry = {
    stage: initialStage,
    changedAt: now,
    changedBy: creator.displayName,
    changedByEmail: creator.email,
    note: "Initial opportunity record created",
  };

  const colRef = collection(db, COLLECTION_NAME);
  const docRef = await addDoc(
    colRef,
    sanitizeForFirestore({
      ...data,
      stage: initialStage,
      stageHistory: [initialHistory],
      isArchived: false,
      createdAt: now,
      updatedAt: now,
    }),
  );

  return docRef.id;
}

/**
 * Transitions an opportunity to a new stage with a timestamped audit entry
 */
export async function transitionOpportunityStage(
  id: string,
  newStage: OpportunityStage,
  user: { displayName: string; email: string },
  note?: string,
  currentHistory: StageHistoryEntry[] = [],
): Promise<void> {
  const now = new Date().toISOString();

  const newEntry: StageHistoryEntry = {
    stage: newStage,
    changedAt: now,
    changedBy: user.displayName,
    changedByEmail: user.email,
    note: note || `Stage updated to ${newStage.replace("_", " ")}`,
  };

  const updatedHistory = [...currentHistory, newEntry];

  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(
    docRef,
    sanitizeForFirestore({
      stage: newStage,
      isArchived: newStage === "archived",
      stageHistory: updatedHistory,
      updatedAt: now,
    }),
  );
}

/**
 * Saves a completed or updated Qualify Checklist and synchronizes the Go / No-Go decision
 */
export async function saveQualifyChecklist(
  id: string,
  checklist: QualifyChecklist,
  user: { displayName: string; email: string },
  currentHistory: StageHistoryEntry[] = [],
): Promise<void> {
  const now = new Date().toISOString();
  const updates: Partial<Opportunity> = {
    qualifyChecklist: checklist,
    updatedAt: now,
  };

  if (checklist.decision === "go") {
    updates.decision = "go";
    updates.stage = "go";
    const historyEntry: StageHistoryEntry = {
      stage: "go",
      changedAt: now,
      changedBy: user.displayName,
      changedByEmail: user.email,
      note: `Qualified as GO by ${checklist.deciderName || user.displayName}: ${checklist.decisionReason || "Meets criteria"}`,
    };
    updates.stageHistory = [...currentHistory, historyEntry];
  } else if (checklist.decision === "no_go") {
    updates.decision = "no_go";
    updates.stage = "no_go";
    updates.noGoReason = checklist.decisionReason || "Declined during qualification";
    const historyEntry: StageHistoryEntry = {
      stage: "no_go",
      changedAt: now,
      changedBy: user.displayName,
      changedByEmail: user.email,
      note: `Qualified as NO-GO by ${checklist.deciderName || user.displayName}: ${checklist.decisionReason || "Declined"}`,
    };
    updates.stageHistory = [...currentHistory, historyEntry];
  }

  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(docRef, sanitizeForFirestore(updates));
}

/**
 * Updates an opportunity's general fields
 */
export async function updateOpportunity(
  id: string,
  updates: Partial<Omit<Opportunity, "id" | "createdAt">>,
): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(
    docRef,
    sanitizeForFirestore({
      ...updates,
      updatedAt: new Date().toISOString(),
    }),
  );
}

/**
 * Archives an opportunity and ends it early with a designated reason and audit trail
 */
export async function archiveOpportunity(
  id: string,
  reason: string,
  user: { displayName?: string | null; email?: string | null },
  currentHistory: StageHistoryEntry[] = [],
  notes?: string,
): Promise<void> {
  const now = new Date().toISOString();
  const userName = user?.displayName || user?.email || "User";
  const userEmail = user?.email || "";

  const noteText = notes
    ? `Archived / Ended Early: ${reason} (${notes})`
    : `Archived / Ended Early: ${reason}`;

  const historyEntry: StageHistoryEntry = {
    stage: "archived",
    changedAt: now,
    changedBy: userName,
    changedByEmail: userEmail,
    note: noteText,
  };

  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(
    docRef,
    sanitizeForFirestore({
      stage: "archived" as OpportunityStage,
      isArchived: true,
      archiveReason: reason + (notes ? `: ${notes}` : ""),
      archivedAt: now,
      stageHistory: [...currentHistory, historyEntry],
      updatedAt: now,
    }),
  );
}

/**
 * Restores an archived opportunity back into an active pipeline stage
 */
export async function unarchiveOpportunity(
  id: string,
  restoreStage: OpportunityStage = "reviewing",
  user: { displayName?: string | null; email?: string | null },
  currentHistory: StageHistoryEntry[] = [],
): Promise<void> {
  const now = new Date().toISOString();
  const userName = user?.displayName || user?.email || "User";
  const userEmail = user?.email || "";

  const historyEntry: StageHistoryEntry = {
    stage: restoreStage,
    changedAt: now,
    changedBy: userName,
    changedByEmail: userEmail,
    note: `Restored from archive to ${restoreStage.replace("_", " ")}`,
  };

  const docRef = doc(db, COLLECTION_NAME, id);
  await updateDoc(
    docRef,
    sanitizeForFirestore({
      stage: restoreStage,
      isArchived: false,
      archiveReason: null,
      archivedAt: null,
      stageHistory: [...currentHistory, historyEntry],
      updatedAt: now,
    }),
  );
}

/**
 * Deletes an opportunity document
 */
export async function deleteOpportunity(id: string): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  await deleteDoc(docRef);
}

/**
 * Applies an AI-extracted key date to the opportunity tracking fields
 */
export async function applyExtractedDate(id: string, date: KeyDate): Promise<void> {
  const docRef = doc(db, COLLECTION_NAME, id);
  const updates: Partial<Opportunity> = {
    updatedAt: new Date().toISOString(),
  };

  if (date.kind === "proposal_due") {
    updates.dueDate = date.date;
  } else if (date.kind === "questions_due") {
    updates.questionsDueDate = date.date;
  }

  await updateDoc(docRef, sanitizeForFirestore(updates));
}

/**
 * Records the final bid outcome and post-mortem debrief,
 * updates the pipeline stage, records an audit trail entry,
 * and calls win credit & harvest outcome stampers.
 */
export async function recordBidOutcome(
  id: string,
  outcome: BidOutcome,
  debriefData: Omit<PostMortemDebrief, "recordedAt" | "recordedBy" | "recordedByEmail">,
  user?: { displayName?: string | null; email?: string | null },
  currentHistory: StageHistoryEntry[] = [],
): Promise<void> {
  const now = new Date().toISOString();
  const userName = user?.displayName || user?.email || "User";
  const userEmail = user?.email || "";

  const fullDebrief: PostMortemDebrief = {
    ...debriefData,
    recordedAt: now,
    recordedBy: userName,
    recordedByEmail: userEmail,
  };

  const newStage: OpportunityStage = outcome === "won" ? "won" : "lost";

  const stageNote =
    outcome === "won"
      ? `Bid Won! Award notice recorded by ${userName}`
      : `Bid marked as ${outcome.toUpperCase()}${debriefData.winningCompetitor ? ` (Competitor: ${debriefData.winningCompetitor})` : ""}`;

  const historyEntry: StageHistoryEntry = {
    stage: newStage,
    changedAt: now,
    changedBy: userName,
    changedByEmail: userEmail,
    note: stageNote,
  };

  const docRef = doc(db, COLLECTION_NAME, id);
  const updatePayload: Record<string, unknown> = {
    outcome,
    stage: newStage,
    postMortem: fullDebrief,
    stageHistory: [...currentHistory, historyEntry],
    updatedAt: now,
  };

  if (debriefData.rebidTracking) {
    updatePayload.rebidTracking = debriefData.rebidTracking;
  }

  await updateDoc(docRef, sanitizeForFirestore(updatePayload));

  // If won, stamp win credit on all library answers actually used
  if (outcome === "won") {
    await stampWinCredit(id);
  }

  // Stamp sourceOutcome on any answers harvested from this proposal
  await stampHarvestOutcome(id, outcome);
}
